import { ArrowRight, X, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FONT_WEIGHTS,
  GAP_SCALE,
  INTERACTIVE_STATES,
  MARGIN_BOTTOM,
  MARGIN_Y,
  PADDING_X,
  PADDING_TOP,
  PADDING_BOTTOM,
  PADDING_RIGHT,
  POSITION_SCALE,
  RADIUS_SCALE,
  SHADOW_SCALE,
  SIZE_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  COLOR_SCALE,
  FLEX,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';
import { createPageUrl } from '@/utils';

/**
 * FloatingCTA Component
 *
 * A sticky call-to-action button that appears after scrolling
 * - Shows after user scrolls past hero section
 * - Dismissible (remembers for session)
 * - Mobile-friendly
 * - Smooth animations
 * - Updated for hot reload testing
 */
function FloatingCTA() {
  const { t } = useTranslation() as {
    t: (page: string, context: string, fallback?: string) => string;
    currentLanguage: string;
    changeLanguage: (lang: string) => void;
    loading: boolean;
    forceUpdate: number;
    translations: Record<string, unknown>;
    fallbackTranslations: Record<string, unknown>;
    debugTranslations: () => void;
  };
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const mobileCtaSafeArea = {
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-md, 16px))',
  };

  useEffect(() => {
    // Check if user has dismissed this session
    const dismissed = sessionStorage.getItem('floatingCTADismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      return;
    }

    const handleScroll = () => {
      // Show after scrolling 300px (roughly past hero section)
      const scrollPosition = window.scrollY;
      const shouldShow = scrollPosition > 300;

      setIsVisible(shouldShow);
    };

    // Throttle scroll events for performance
    let rafId: number | null = null;
    const throttledScroll = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    // Check initial position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('floatingCTADismissed', 'true');
  };

  const handleCTAClick = () => {
    void navigate(createPageUrl('Providers'));
  };

  if (isDismissed) return null;

  return (
    <>
      {/* Desktop Version - Bottom Left */}
      <div
        className={`hidden lg:block fixed bottom-6 left-6 z-fab transition-all ${INTERACTIVE_STATES.duration.slow} ${
          isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none'
        }`}
      >
        <div
          className={`backdrop-blur-xl bg-gradient-to-br from-blue-500/20 to-blue-600/30 border border-white/30 ${RADIUS_SCALE['2xl']} ${SHADOW_SCALE['2xl']} overflow-hidden max-w-sm group hover:from-blue-500/25 hover:to-blue-600/35 hover:border-white/40 transition-all ${INTERACTIVE_STATES.duration.slow}`}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className={`absolute ${POSITION_SCALE.top['3']} ${POSITION_SCALE.right['3']} ${SURFACE_PADDING['2']} hover:${COLOR_SCALE.surface}/20 ${RADIUS_SCALE.full} ${INTERACTIVE_STATES.duration.normal} z-10`}
            aria-label={t('common', 'close', 'Close')}
          >
            <X className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['gray-800']}`} />
          </button>

          {/* Content */}
          <div className={`${SURFACE_PADDING['4']} ${PADDING_X['8']}`}>
            <div className={`flex items-start ${GAP_SCALE['4']}`}>
              {/* Icon */}
              <div
                className={`flex-shrink-0 ${SIZE_SCALE.w12} ${SIZE_SCALE['12']} ${COLOR_SCALE.surface}/60 backdrop-blur-sm ${RADIUS_SCALE.xl} flex items-center justify-center border border-white/50`}
                aria-hidden="true"
              >
                <Search
                  className={`${SIZE_SCALE['6']} ${SIZE_SCALE['6']} ${COLOR_SCALE.foreground}`}
                  aria-hidden="true"
                />
              </div>

              {/* Text */}
              <div className={FLEX.flex1}>
                <h4
                  className={`${TYPE_SCALE.base} font-semibold ${MARGIN_Y['1']} ${COLOR_SCALE.foreground} drop-shadow-sm`}
                >
                  {t('floating_cta', 'title', 'Ready to Get Started?')}
                </h4>
                <p
                  className={`${TYPE_SCALE.sm} ${COLOR_SCALE['gray-700']} ${MARGIN_BOTTOM['3']} drop-shadow-sm`}
                >
                  {t(
                    'floating_cta',
                    'description',
                    'Browse verified providers and find your perfect match'
                  )}
                </p>

                {/* CTA Button */}
                <button
                  onClick={handleCTAClick}
                  className={`inline-flex items-center ${GAP_SCALE['2']} ${COLOR_SCALE.surface}/90 backdrop-blur-sm ${COLOR_SCALE.foreground} ${SURFACE_PADDING['4']} ${RADIUS_SCALE.lg} ${TYPE_SCALE.sm} font-semibold hover:${COLOR_SCALE.surface} transition-all ${INTERACTIVE_STATES.duration.normal} hover:${GAP_SCALE['3']} group/btn ${SHADOW_SCALE.lg}`}
                >
                  {t('floating_cta', 'button', 'Find Providers')}
                  <ArrowRight
                    className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} transition-transform ${INTERACTIVE_STATES.duration.normal} group-hover/btn:translate-x-0.5`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Decorative element */}
          <div
            className={`absolute top-0 right-0 ${SIZE_SCALE.w32} ${SIZE_SCALE['32']} bg-gradient-to-br from-white/20 to-transparent rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500`}
          ></div>
        </div>
      </div>

      {/* Mobile Version - Bottom Fixed Bar */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-fab transition-all ${INTERACTIVE_STATES.duration.slow} ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'
        }`}
      >
        <div
          className={`bg-gradient-to-r from-gray-900 to-gray-800 text-white ${SHADOW_SCALE['2xl']} border-t ${COLOR_SCALE['tooltip-border']}`}
          style={mobileCtaSafeArea}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className={`absolute top-2 right-2 ${SURFACE_PADDING['3.5']} hover:${COLOR_SCALE.surface}/20 rounded-full ${INTERACTIVE_STATES.duration.normal}`}
            aria-label={t('common', 'close', 'Close')}
          >
            <X className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`} aria-hidden="true" />
          </button>

          {/* Content */}
          <div
            className={`${PADDING_X['4']} ${PADDING_TOP['4']} ${PADDING_BOTTOM['2']} ${PADDING_RIGHT['12']}`}
          >
            <div className={`flex items-center justify-between ${GAP_SCALE['3']}`}>
              <div className={`${FLEX.flex1} ${SIZE_SCALE.minW0}`}>
                <h4
                  className={`text-sm ${FONT_WEIGHTS.semibold} ${MARGIN_BOTTOM['0.5']} text-white`}
                >
                  {t('floating_cta', 'title', 'Ready to Get Started?')}
                </h4>
                <p className={`${TYPE_SCALE.xs} ${COLOR_SCALE['blue-100']}`}>
                  {t('floating_cta', 'mobile_description', 'Find your perfect provider')}
                </p>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleCTAClick}
                className={`flex-shrink-0 ${COLOR_SCALE.surface} ${COLOR_SCALE.foreground} ${SURFACE_PADDING['4']} ${RADIUS_SCALE.lg} text-sm font-semibold hover:${COLOR_SCALE['surface-subtle']} transition-all ${INTERACTIVE_STATES.duration.normal} inline-flex items-center ${GAP_SCALE['2']}`}
              >
                {t('floating_cta', 'button_short', 'Browse')}
                <ArrowRight className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const FloatingCTAWithMonitor = withMemoryLeakMonitor(FloatingCTA, {
  componentName: 'FloatingCTA',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: false, // Only renders on scroll events
});

export { FloatingCTAWithMonitor as FloatingCTA };
